import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ArrowRight } from "lucide-react";
import { useVehicle } from "../../../lib/vehicleContext";
import { nearestKnownCity } from "../../../lib/geoLookup";
import { computeRangeScenario } from "../../../lib/rangeModel";
import {
  Slider,
  LabeledInput,
  TripStat,
  StatCard,
  TRIP_PRESETS,
  estimateMiles,
} from "./_shared";

export const TripView = () => {
  const { t } = useTranslation();
  const { vehicle } = useVehicle();

  const [presetKey, setPresetKey] = useState("sf-tahoe");
  const [origin, setOrigin] = useState("San Francisco");
  const [destination, setDestination] = useState("South Lake Tahoe");
  const [tripMiles, setTripMiles] = useState(196);
  const [milesAuto, setMilesAuto] = useState(true);
  const [startPct, setStartPct] = useState(90);

  // Auto-snap origin to Tesla live vehicle location one time.
  const [originAutoLocked, setOriginAutoLocked] = useState(false);
  useEffect(() => {
    if (originAutoLocked || !vehicle?.live) return;
    const lat = vehicle.location?.lat;
    const lng = vehicle.location?.lng;
    if (typeof lat !== "number" || typeof lng !== "number") return;
    const near = nearestKnownCity(lat, lng);
    setOrigin(near || `Vehicle location`);
    setPresetKey("custom");
    setOriginAutoLocked(true);
    if (typeof vehicle.usableBatteryPct === "number") {
      setStartPct(Math.max(50, Math.min(100, Math.round(vehicle.usableBatteryPct))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.live, vehicle?.location?.lat, vehicle?.location?.lng]);

  useEffect(() => {
    if (!milesAuto) return;
    const est = estimateMiles(origin, destination);
    if (est !== null && est !== tripMiles) {
      setTripMiles(est);
    }
  }, [origin, destination, milesAuto, tripMiles]);

  const applyPreset = (key) => {
    const p = TRIP_PRESETS.find((x) => x.key === key) || TRIP_PRESETS[0];
    setPresetKey(key);
    if (key !== "custom") {
      setOrigin(p.origin);
      setDestination(p.dest);
      setTripMiles(p.miles);
      setMilesAuto(true);
    }
  };

  const scenario = {
    tempF: 72,
    speedMph: 68,
    hvac: "mild",
    elevationGain: 2200,
    cargoLbs: 150,
    wheels: "aero",
    bufferPct: 15,
  };
  const range = computeRangeScenario(vehicle, scenario);
  const stopsNeeded = Math.max(0, Math.ceil(tripMiles / range.usableRange) - 1);
  const arrivePct = Math.max(
    5,
    Math.round(startPct - (tripMiles / range.usableRange) * 80)
  );
  const energyKwh = Math.max(1, Math.round((tripMiles / range.usableRange) * 75));
  const cost = (energyKwh * 0.35).toFixed(2);
  const willMake = arrivePct >= 10 && stopsNeeded === 0;

  return (
    <div className="pt-4 space-y-4" data-testid="mobile-app-view-trip">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          {t("app.trip_eyebrow", "Trip Planner")}
        </div>
        <div className="font-display text-lg text-white font-semibold leading-tight">
          {t("app.trip_headline_1", "Real routes,")}{" "}
          <span className="brand-text-gradient">
            {t("app.trip_headline_2", "your battery reality.")}
          </span>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2.5">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
            Route preset
          </div>
          <select
            value={presetKey}
            onChange={(e) => applyPreset(e.target.value)}
            data-testid="trip-preset-select"
            className="w-full appearance-none bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#39FF14]/40 cursor-pointer"
          >
            {TRIP_PRESETS.map((p) => (
              <option key={p.key} value={p.key} className="bg-[#0a0a0a]">
                {p.key === "custom" ? "Custom route" : `${p.origin} → ${p.dest}`}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <LabeledInput
            label="From"
            testid="trip-origin"
            value={origin}
            onChange={(v) => {
              setOrigin(v);
              setPresetKey("custom");
            }}
            placeholder="Origin"
          />
          <LabeledInput
            label="To"
            testid="trip-destination"
            value={destination}
            onChange={(v) => {
              setDestination(v);
              setPresetKey("custom");
            }}
            placeholder="Destination"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Slider
            label="Trip miles"
            testid="trip-miles-slider"
            value={tripMiles}
            min={20}
            max={Math.max(500, tripMiles)}
            step={5}
            onChange={(v) => {
              setTripMiles(v);
              setMilesAuto(false);
              setPresetKey("custom");
            }}
            suffix="mi"
          />
          <Slider
            label="Start SoC"
            testid="trip-soc-slider"
            value={startPct}
            min={30}
            max={100}
            step={5}
            onChange={setStartPct}
            suffix="%"
          />
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-mono text-xs text-white truncate flex-1" data-testid="trip-origin-display">
            {origin || "—"}
          </span>
          <div className="flex-1 h-px border-t border-dashed border-white/20 relative">
            <ChevronRight
              className="w-3 h-3 absolute -right-1 -top-1.5 text-zinc-500"
              strokeWidth={2}
            />
          </div>
          <span className="font-mono text-xs text-white truncate flex-1 text-right" data-testid="trip-destination-display">
            {destination || "—"}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <TripStat label="Miles" val={String(tripMiles)} />
          <TripStat
            label="Stops"
            val={String(stopsNeeded)}
            color={stopsNeeded === 0 ? "#39FF14" : "#FFB020"}
          />
          <TripStat label="Vehicle" val={vehicle.short} />
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="font-mono text-[10px] uppercase text-zinc-500">Battery plan</span>
          <span
            className="font-mono text-[10px] text-zinc-400"
            data-testid="trip-battery-plan"
          >
            {startPct}% → {arrivePct}%
          </span>
        </div>
        <div className="h-3 rounded-full bg-white/5 overflow-hidden flex">
          <div className="h-full" style={{ width: `${arrivePct}%`, background: "#00E5FF" }} />
          <div
            className="h-full"
            style={{ width: `${Math.max(0, startPct - arrivePct)}%`, background: "#39FF14" }}
          />
          <div
            className="h-full"
            style={{ width: `${Math.max(0, 100 - startPct)}%`, background: "rgba(57,255,20,0.15)" }}
          />
        </div>
      </div>

      <div
        className="rounded-xl border p-3"
        style={{
          borderColor: willMake ? "rgba(57,255,20,0.35)" : "rgba(255,176,32,0.35)",
          background: willMake ? "rgba(57,255,20,0.05)" : "rgba(255,176,32,0.06)",
        }}
        data-testid="trip-verdict"
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: willMake ? "#39FF14" : "#FFB020",
              boxShadow: `0 0 8px ${willMake ? "#39FF14" : "#FFB020"}`,
            }}
          />
          <span className="font-mono text-[10px] uppercase text-white">
            {willMake ? "Will make it" : `Needs ${stopsNeeded} charge stop${stopsNeeded > 1 ? "s" : ""}`}
          </span>
        </div>
        <div className="text-[11px] text-zinc-300 leading-tight">
          {willMake
            ? `Arrive with ~${arrivePct}% SoC. No charging stops required.`
            : `On-route Superchargers along ${origin || "your route"} → ${destination || "destination"} will keep you moving.`}
        </div>
      </div>

      {stopsNeeded > 0 && (
        <div className="rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/[0.03] p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-2 h-2 rounded-full bg-[#00E5FF] shadow-[0_0_8px_#00E5FF]" />
            <span className="text-sm text-white font-semibold">Suggested stop</span>
            <span className="ml-auto font-mono text-[10px] text-zinc-400">~20 min</span>
          </div>
          <div className="text-xs text-zinc-400 leading-relaxed">
            Charge 22% → 78% · Estimated cost{" "}
            <span className="text-white">${((energyKwh / 2) * 0.35).toFixed(2)}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Energy"
          value={`${energyKwh} kWh`}
          trend={`${(tripMiles / energyKwh).toFixed(2)} mi/kWh`}
          color="#00E5FF"
        />
        <StatCard
          label="Total cost"
          value={`$${cost}`}
          trend={`vs $${(tripMiles * 0.21).toFixed(0)} gas`}
          color="#39FF14"
        />
      </div>

      <button
        className="w-full brand-gradient text-black font-semibold rounded-xl py-3 text-sm inline-flex items-center justify-center gap-2"
        data-testid="trip-share-cta"
      >
        Share plan
        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </div>
  );
};
